import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef, EventEmitter, HostBinding,
    HostListener,
    Inject,
    Input, OnInit,
    Optional, Output,
    ViewChild
} from '@angular/core';
import {ModalToolContainerService, WebmateToolConfig} from '../modaltoolcontainer/modaltoolcontainer.service';
import {TOOL_CONTAINER_DATA} from '../modaltoolcontainer/modaltoolcontainer.component';
import {ImageUrl, ProjectId, ScreenshotId} from '@webmate-web-core/shared/types/ids';
import {ImageService} from '@webmate-web-core/shared/services/image.service';
import {
    faEdit,
    faEye,
    faEyeSlash,
    faLongArrowRight,
    faMousePointer,
    faSave,
    faTimes,
    faTrash
} from '@fortawesome/pro-solid-svg-icons';
import {faArrowDown, faArrowUp, faRectangleLandscape, faText} from '@fortawesome/pro-light-svg-icons';
import {ImageMetadata, ImageOverlay} from '@webmate-web-core/shared/api/rest/image.restapi';
import {getSmallId} from '@webmate-web-core/shared/utils/utils';
import {SmallId} from '@webmate-web-core/shared/types/base-types';
import * as paper from 'paper';
import {OverlayHitEvent} from '@webmate-web-core/shared/components/screenshot/canvas-based-image.component';
import _ from 'lodash';
import {switchMap} from 'rxjs';

export class ImageEditorToolConfig implements WebmateToolConfig {
    toolName = "Image Editor";

    /**
     * Screenshot viewer can be used to show an image from webmate image service or from a given image URL (such as a data URL).
     */
    constructor(
        public readonly imageId: ScreenshotId,
        public readonly imageUrl?: string,
        public readonly projectId?: ProjectId,
        public readonly overlaysChangedCallback?: (meta: ImageMetadata) => void,
        public readonly insidePopup: boolean = true,
        ) {}
}

type ToolName = 'arrow' | 'rectangle' | 'text' | 'pointer';

abstract class ImageEditorTool {
    name: ToolName;

    overlayId: SmallId | undefined;
    start: {x: number, y: number} | undefined;

    protected constructor(protected comp: ImageEditorToolComponent) {}

    onPaperHitEvent(event: OverlayHitEvent, overlay?: ImageOverlay) {
        switch (event.paperEvent.type) {
            case 'mousedrag':
                this.handleMouseDrag(event, overlay);
                break;

            case 'mousedown':
                this.handleMouseDown(event, overlay);
                break;

            case 'mouseup':
                this.handleMouseUp(event, overlay);
                break;
        }
    }


    handleMouseDown(event: OverlayHitEvent, overlay?: ImageOverlay) {

        if (overlay) {
            this.comp.activateOverlay(overlay);
        } else {
            this.overlayId = getSmallId();
            this.start = {
                x: event.paperEvent.point.x / event.boundingBox.width,
                y: event.paperEvent.point.y / event.boundingBox.height
            };
        }
    }

    handleMouseUp(event: OverlayHitEvent, overlay?: ImageOverlay) {
        // create new overlay
        if (this.start && this.overlayId) {
            const end = {x: event.paperEvent.point.x / event.boundingBox.width, y: event.paperEvent.point.y / event.boundingBox.height};

            // ignore small rectangles
            if (Math.abs(end.x - this.start.x) < 10 / event.boundingBox.width && Math.abs(end.y - this.start.y) < 10 / event.boundingBox.height) {
                this.comp.deleteOverlay(this.overlayId);
            }
            const overlayId = this.overlayId;
            this.start = undefined;
            this.overlayId = undefined
            this.comp.activateOverlayById(overlayId);
        }
    }

    handleMouseDrag(event: OverlayHitEvent, overlay?: ImageOverlay): void {

    }
}

class ArrowTool extends ImageEditorTool {
    name: ToolName = 'arrow';

    constructor(comp: ImageEditorToolComponent) {
        super(comp);
    }

    override handleMouseDrag(event: OverlayHitEvent, overlay?: ImageOverlay) {
        if (this.start && this.overlayId) {
            const end = {x: event.paperEvent.point.x / event.boundingBox.width, y: event.paperEvent.point.y / event.boundingBox.height};

            this.comp.setOverlay({
                id: this.overlayId,
                overlayData: {
                    type: 'ArrowOverlayData',
                    start: this.start,
                    end
                }
            });
        }
    }
}

class RectangleTool extends ImageEditorTool {
    name: ToolName = 'rectangle';

    constructor(comp: ImageEditorToolComponent) {
        super(comp);
    }

    handleMouseDrag(event: OverlayHitEvent, overlay?: ImageOverlay) {
        if (this.start && this.overlayId) {
            const end = {x: event.paperEvent.point.x / event.boundingBox.width, y: event.paperEvent.point.y / event.boundingBox.height};

            this.comp.setOverlay({
                id: this.overlayId,
                overlayData: {
                    type: 'RectangleOverlayData',
                    rectangle: {
                        x: Math.min(this.start.x, end.x),
                        y: Math.min(this.start.y, end.y),
                        width: Math.abs(end.x - this.start.x),
                        height: Math.abs(end.y - this.start.y)
                    }
                }
            });
        }
    }
}

class TextTool extends ImageEditorTool {
    name: ToolName = 'text';

    constructor(comp: ImageEditorToolComponent) {
        super(comp);
    }

    override handleMouseDown(event: OverlayHitEvent, overlay?: ImageOverlay) {
        this.overlayId = getSmallId();
        const position = {x: event.paperEvent.point.x / event.boundingBox.width, y: event.paperEvent.point.y / event.boundingBox.height };

        this.comp.setOverlay({
            id: this.overlayId,
            overlayData: {
                type: 'TextOverlayData',
                text: 'Text',
                position: position
            }
        });

        this.comp.activateOverlayById(this.overlayId);
    }

    override handleMouseUp(event: OverlayHitEvent, overlay?: ImageOverlay) {
        // do nothing
    }

    override handleMouseDrag(event: OverlayHitEvent, overlay?: ImageOverlay) {
        // do nothing
    }
}

class PointerTool extends ImageEditorTool {
    name: ToolName = 'pointer';

    constructor(comp: ImageEditorToolComponent) {
        super(comp);
    }

    override onPaperHitEvent(event: OverlayHitEvent, overlay?: ImageOverlay) {
        if (!overlay) {
            this.comp.activateOverlay(undefined);
            return;
        }

        switch (event.paperEvent.type) {
            case 'mousedrag':
                this.handleMouseDrag(event, overlay);
                break;

            case 'mousedown':
                this.comp.activateOverlay(overlay);
                break;
        }
    }

    override handleMouseDrag(event: OverlayHitEvent, overlay?: ImageOverlay) {
        const itemId = event.overlayId;
        if (!overlay || !itemId) {
            return;
        }

        const boundingBox = event.boundingBox;

        if (overlay.overlayData.type === 'ArrowOverlayData') {
            if (event.overlayHandleType === 'start') {
                this.comp.patchOverlayData(itemId, {
                    start: {x: event.paperEvent.point.x / boundingBox.width, y: event.paperEvent.point.y / boundingBox.height }
                });
            } else if (event.overlayHandleType === 'end') {
                this.comp.patchOverlayData(itemId, {
                    end: {x: event.paperEvent.point.x / boundingBox.width, y: event.paperEvent.point.y / boundingBox.height }
                });
            }
        } else if (overlay.overlayData.type === 'RectangleOverlayData') {
            const overlayData = overlay.overlayData as any;
            if (event.overlayHandleType === 'topLeft') {
                this.comp.patchOverlayData(itemId, {
                    rectangle: {
                        x: event.paperEvent.point.x / boundingBox.width,
                        y: event.paperEvent.point.y / boundingBox.height,
                        width: overlayData.rectangle.width + overlayData.rectangle.x  - (event.paperEvent.point.x / boundingBox.width),
                        height: overlayData.rectangle.height + overlayData.rectangle.y - (event.paperEvent.point.y / boundingBox.height)
                    }
                });
            } else if (event.overlayHandleType === 'bottomRight') {
                this.comp.patchOverlayData(itemId, {
                    rectangle: {
                        x: overlayData.rectangle.x,
                        y: overlayData.rectangle.y,
                        width: (event.paperEvent.point.x / boundingBox.width) - overlayData.rectangle.x,
                        height: (event.paperEvent.point.y / boundingBox.height) - overlayData.rectangle.y
                    }
                });
            } else if (event.overlayHandleType === 'topRight') {
                this.comp.patchOverlayData(itemId, {
                    rectangle: {
                        x: overlayData.rectangle.x,
                        y: event.paperEvent.point.y / boundingBox.height,
                        width: (event.paperEvent.point.x / boundingBox.width) - overlayData.rectangle.x,
                        height: overlayData.rectangle.height + overlayData.rectangle.y - (event.paperEvent.point.y / boundingBox.height)
                    }
                });
            } else if (event.overlayHandleType === 'bottomLeft') {
                this.comp.patchOverlayData(itemId, {
                    rectangle: {
                        x: (event.paperEvent.point.x / boundingBox.width),
                        y: overlayData.rectangle.y,
                        width: overlayData.rectangle.width + overlayData.rectangle.x  - (event.paperEvent.point.x / boundingBox.width),
                        height: (event.paperEvent.point.y / boundingBox.height) - overlayData.rectangle.y
                    }
                });
            }
        }

        if (!event.overlayHandleType) {
            // we move the entire overlay
            const overlayData = overlay.overlayData as any;
            const deltaX = event.paperEvent.delta.x / boundingBox.width;
            const deltaY = event.paperEvent.delta.y / boundingBox.height;

            if (overlayData.type === 'ArrowOverlayData') {
                this.comp.patchOverlayData(itemId, {
                    start: {x: overlayData.start.x + deltaX, y: overlayData.start.y + deltaY},
                    end: {x: overlayData.end.x + deltaX, y: overlayData.end.y + deltaY}
                });
            } else if (overlayData.type === 'RectangleOverlayData') {
                this.comp.patchOverlayData(itemId, {
                    rectangle: {
                        x: overlayData.rectangle.x + deltaX,
                        y: overlayData.rectangle.y + deltaY,
                        width: overlayData.rectangle.width,
                        height: overlayData.rectangle.height
                    }
                });
            } else if (overlayData.type === 'TextOverlayData') {
                this.comp.patchOverlayData(itemId, {
                    position: {
                        x: overlayData.position.x + deltaX,
                        y: overlayData.position.y + deltaY
                    }
                });
            }
        }
    }
}

@Component({
    selector: "wm-imageeditor",
    templateUrl: "./image-editor.component.html",
    styleUrls: ['./image-editor.component.less']
})
export class ImageEditorToolComponent implements OnInit, AfterViewInit {
    @Input() data?: ImageEditorToolConfig;
    @Input() fillContainer: boolean = false;
    @Output() requestToClose = new EventEmitter<void>();

    imageUrl?: ImageUrl;
    downloadUrl?: ImageUrl;
    errorText: string | undefined;

    isDeleteActionEnabled: boolean = true;

    activeTool: ImageEditorTool;

    editOverlays?: ImageOverlay[];
    activeOverlay?: ImageOverlay;

    shownOverlays: ImageOverlay[];

    overlayHandlesFor: SmallId[] = [];

    dirty: boolean = false;

    metadataOverlays: ImageOverlay[] | undefined;

    @ViewChild('imageContainer') imageContainer: ElementRef | undefined;

    containerBounds: paper.Rectangle | undefined;

    @HostBinding('class.inside-popup') get getPopUpBasedStyle() {
        return this.injectedData?.insidePopup;
    }

    constructor(private imageService: ImageService,
                private cd: ChangeDetectorRef,
                private modalToolService: ModalToolContainerService,
                @Optional() @Inject(TOOL_CONTAINER_DATA) public injectedData?: ImageEditorToolConfig) {
    }

    getData() {
        if (!this.data && !this.injectedData) {
            console.error('ImageEditorToolComponent: Image could not be loaded. No image data given.');
            return;
        }
        if (!this.data && this.injectedData) {
            this.data = this.injectedData;
        }
        if (!this.data?.imageId) {
            console.error('ImageEditorToolComponent: No image id given.');
            return;
        }
        this.imageUrl = this.imageService.getImageUrl(this.data.imageId);
        this.downloadUrl = this.imageService.getImageUrl(this.data.imageId, undefined, true);

        this.imageService.getImageMetadata(this.data.imageId).subscribe(metadata => {
            this.editOverlays = metadata?.overlays || [];
            this.metadataOverlays = metadata?.overlays;
            this.showOverlays();
        });
        if (!this.imageUrl) {
            console.error("ImageEditorToolComponent: Image could not be loaded. Image URL could not found");
        }
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
      if ((event.key === 'Delete' || event.key === 'Backspace' ) && this.isDeleteActionEnabled) {
        this.onDeleteKeyPressed();
      }
    }

    ngOnInit() {
        this.getData();
        this.activateTool('arrow');
    }

    ngAfterViewInit() {
        if (this.imageContainer) {
            const bounds = this.imageContainer.nativeElement.getBoundingClientRect();
            this.containerBounds = new paper.Rectangle(bounds.left, bounds.top, bounds.width, bounds.height);
        }
    }

    activateOverlay(overlay?: ImageOverlay) {
        this.activeOverlay = overlay;
        this.overlayHandlesFor = overlay ? [overlay.id] : [];
        this.isDeleteActionEnabled = !!overlay && overlay.overlayData.type !== 'TextOverlayData'; // text overlays cannot be deleted by pressing delete key (don't want to interfere with text editing)
        this.activatePointerTool();
    }

    activateOverlayById(overlayId: SmallId) {
        const overlay = this.shownOverlays.find(item => item.id === overlayId);
        if (overlay) {
            this.activateOverlay(overlay);
        }
    }

    changeColor(color: string) {
        if (this.activeOverlay) {
            switch (this.activeOverlay.overlayData.type) {
                case "RectangleOverlayData":
                    this.patchOverlayData(this.activeOverlay.id, {
                        color: color
                    });
                    break;

                case "ArrowOverlayData":
                    this.patchOverlayData(this.activeOverlay.id, {
                        color: color
                    });
                    break;

                case "TextOverlayData":
                    this.patchOverlayData(this.activeOverlay.id, {
                        textColor: color
                    });
                    break;
            }
        }
        this.showOverlays();
    }

    activatePointerTool() {
        this.activateTool('pointer');
    }

    activateTool(toolName: ToolName) {
        switch (toolName) {
            case 'arrow':
                this.overlayHandlesFor = [];
                this.activeTool = new ArrowTool(this);
                break;
            case 'rectangle':
                this.overlayHandlesFor = [];
                this.activeTool = new RectangleTool(this);
                break;
            case 'text':
                this.overlayHandlesFor = [];
                this.activeTool = new TextTool(this);
                break;
            case 'pointer':
                this.activeTool = new PointerTool(this);
                break;
        }
    }

    save() {
        if (this.data?.imageId) {
            this.imageService.setImageOverlays(this.data.imageId!, this.editOverlays || []).pipe(
                switchMap(_ => this.imageService.getImageMetadata(this.data!.imageId!))
            ).subscribe(metadata => {
                this.metadataOverlays = metadata?.overlays;
                this.showOverlays();
                this.data!.overlaysChangedCallback?.(metadata!);
            });
        }
    }

    cancel() {
        // The editor might have been opened on a popup
        this.modalToolService.closeLastTool();
        this.requestToClose.emit();
    }

    increaseFontSize() {
        if (this.activeOverlay && this.activeOverlay.overlayData.type === 'TextOverlayData') {
            this.activeOverlay.overlayData.fontSize = (this.activeOverlay.overlayData.fontSize || 32) + 2;
            this.patchOverlayData(this.activeOverlay.id, {
                fontSize: `${this.activeOverlay.overlayData.fontSize}px`
            });
        }
    }

    decreaseFontSize() {
        if (this.activeOverlay && this.activeOverlay.overlayData.type === 'TextOverlayData') {
            this.activeOverlay.overlayData.fontSize = (this.activeOverlay.overlayData.fontSize || 32) - 2;
            this.patchOverlayData(this.activeOverlay.id, {
                fontSize: `${this.activeOverlay.overlayData.fontSize}px`
            });
        }
    }


    onDeleteKeyPressed() {
        if (this.activeOverlay) {
            this.deleteOverlay(this.activeOverlay.id);
        }
    }

    onOverlayHit(event: OverlayHitEvent) {
        const overlay = this.shownOverlays?.find(item => item.id === event.overlayId);
        this.activeTool.onPaperHitEvent(event, overlay);
    }

    toggleHideOverlay() {
        if (this.activeOverlay) {
            this.activeOverlay.hidden = !this.activeOverlay.hidden;
            this.setOverlayHidden(this.activeOverlay.id, this.activeOverlay.hidden);
        }
        this.showOverlays();
    }

    deleteOverlay(id: SmallId) {
        if (!this.editOverlays) {
            return;
        }
        this.editOverlays = this.editOverlays.filter(item => item.id !== id);
        this.activeOverlay = undefined;
        this.showOverlays();
    }

    setOverlay(overlay: ImageOverlay) {
        if (!this.editOverlays) {
            this.editOverlays = [];
        }

        const existingOverlay = this.editOverlays.find(item => item.id === overlay.id);
        if (existingOverlay) {
            this.editOverlays = this.editOverlays.filter(item => item.id !== overlay.id);
        }

        if (!overlay.order) {
            overlay.order = 0;
        }
        if (!overlay.hidden) {
            overlay.hidden = false;
        }

        this.editOverlays.push(overlay);

        this.showOverlays();
    }

    patchOverlayData(overlayId: SmallId, overlayPatch: any) {
        if (!this.editOverlays) {
            return;
        }

        const existingOverlay = this.editOverlays.find(item => item.id === overlayId);
        if (existingOverlay) {
            this.editOverlays = this.editOverlays.filter(item => item.id !== overlayId);
        }

        this.editOverlays.push({
            id: overlayId,
            hidden: existingOverlay?.hidden || false,
            order: existingOverlay?.order || 0,
            overlayData: {...(existingOverlay?.overlayData || {}), ...overlayPatch}
        });

        this.showOverlays();
    }

    setOverlayHidden(overlayId: SmallId, hidden: boolean) {
        if (!this.editOverlays) {
            return;
        }

        const existingOverlay = this.editOverlays.find(item => item.id === overlayId);
        if (existingOverlay) {
            this.editOverlays = this.editOverlays.filter(item => item.id !== overlayId);
            this.editOverlays.push({
                id: overlayId,
                hidden: hidden,
                overlayData: existingOverlay.overlayData
            });
        }

        this.showOverlays();
    }

    showOverlays() {
        // this.shownOverlays = (this.editOverlays || []).concat(this.previewOverlay ? [this.previewOverlay] : []);
        this.shownOverlays = [...(this.editOverlays || [])];

        this.dirty = !_.isEqual(this.editOverlays, this.metadataOverlays);

        // needed to trigger change detection in image component for repainting overlays
        this.cd.markForCheck();
    }


    protected readonly confirm = confirm;
    protected readonly faEdit = faEdit;
    protected readonly faLongArrowRight = faLongArrowRight;
    protected readonly faRectangleLandscape = faRectangleLandscape;
    protected readonly faText = faText;
    protected readonly faSave = faSave;
    protected readonly faTimes = faTimes;
    protected readonly faMousePointer = faMousePointer;
    protected readonly faTrash = faTrash;
    protected readonly faArrowUp = faArrowUp;
    protected readonly faArrowDown = faArrowDown;
    protected readonly faEyeSlash = faEyeSlash;
    protected readonly faEye = faEye;
}
